import {
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';

import { TextDetailsTuple } from '@/components/text/textDetailsTuple';
import { volumePresets } from '@/components/volumes/volumeSettings';
import { useViewParameters } from '@/contexts/navigation';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useWorkspace } from '@/contexts/useWorkspace';
import { fileSizeFormatter } from '@/utils/fileSizeFormatter';

import { Panels } from '../../../panels';

export const DatabaseSection = ({
  resource,
}: {
  resource: InventoryResource;
}) => {
  const { rightPanel } = useWorkspace();
  const { removeParam, setParams } = useViewParameters();
  const { AuroraGlobalDbDiscoverySupport } = useFeatureFlags();

  const onClose = () => {
    rightPanel.setIsOpen(false);
    removeParam('id');
  };

  const onBack = () => {
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: resource.id,
        onClose,
        initialTab: 'overview',
      },
    });
  };

  const updateOpenedResource = (resourceId: string) => {
    setParams((state) => ({ ...state, id: resourceId }));
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: resourceId,
        onClose,
        initialTab: 'overview',
        onBack,
      },
    });
  };

  return (
    <Table
      className='w-full'
      sx={{
        '& .props-col': {
          verticalAlign: 'top',
          width: '220px',
          paddingLeft: '40px',
        },
      }}
    >
      <TableBody>
        <TableRow>
          <TableCell className='props-col'>Database name</TableCell>
          <TableCell>
            {resource.resourceProperties?.awsRds?.databaseName}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Cluster</TableCell>
          <TableCell>
            {resource.resourceProperties?.awsRds?.clusterId}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Instance class</TableCell>
          <TableCell>
            {resource.resourceProperties?.awsRds?.instanceClass}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Multi AZ</TableCell>
          <TableCell className='capitalize'>
            {Boolean(resource.resourceProperties?.awsRds?.isMultiAZ)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Publicly accessible</TableCell>
          <TableCell className='capitalize'>
            {Boolean(resource.resourceProperties?.awsRds?.isPubliclyAccessible)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Type</TableCell>
          <TableCell>
            {resource.resourceProperties?.awsRds?.storageSettings?.type &&
              volumePresets[
                resource.resourceProperties?.awsRds?.storageSettings?.type
              ]?.displayName}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Size</TableCell>
          <TableCell>
            {resource.resourceProperties?.awsRds?.sizeBytes !== undefined &&
              fileSizeFormatter(resource.resourceProperties?.awsRds?.sizeBytes)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>IOPS</TableCell>
          <TableCell>
            {resource.resourceProperties?.awsRds?.storageSettings?.iops}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Throughput</TableCell>
          <TableCell>{`${resource.resourceProperties?.awsRds?.storageSettings?.throughput} MB/s`}</TableCell>
        </TableRow>
        {AuroraGlobalDbDiscoverySupport &&
          resource.resourceProperties?.awsRds?.aurora
            ?.globalClusterIdentifier && (
            <TableRow>
              <TableCell className='props-col'>Global database name</TableCell>
              <TableCell>
                {
                  resource.resourceProperties?.awsRds?.aurora
                    ?.globalClusterIdentifier
                }
              </TableCell>
            </TableRow>
          )}
        {resource.resourceProperties?.awsRds?.aurora?.globalDbRole && (
          <TableRow>
            <TableCell className='props-col'>Global DB Role</TableCell>
            <TableCell>
              {resource.resourceProperties?.awsRds?.aurora?.globalDbRole}
            </TableCell>
          </TableRow>
        )}
        {!!resource.relatedGlobalDbClusters?.length && (
          <TableRow>
            <TableCell className='props-col'>
              {resource.resourceProperties?.awsRds?.aurora?.globalDbRole ===
              'secondary'
                ? 'Primary DB clusters'
                : 'Secondary DB clusters'}
            </TableCell>
            <TableCell>
              <Stack
                gap='8px'
                divider={<Divider sx={{ borderStyle: 'dashed' }} />}
              >
                {resource.relatedGlobalDbClusters?.map((cluster) => (
                  <TextDetailsTuple
                    key={cluster.id}
                    primaryProp={cluster.name}
                    onPrimaryClick={() => updateOpenedResource(cluster.id)}
                    secondaryProp={cluster.region}
                    copyable={true}
                  />
                ))}
              </Stack>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};
